import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Attachment } from '../../../core/models/attachment/attachment';
import { PopupService } from '../../../shared/services/popup/popup.service';
import { VideoFileFormat, PdfAndXlsxFileFormat } from '../../../core/services/constant';
import { RaiseFundApplicationActionType } from '../../../core/enum/raise-fund-application-action-type.enum';
import { FundRaiseProcess } from '../../../core/models/raise/fund-raise-process';
import { TabContent } from '../../../core/models/raise/tab-content';
import { ActivatedRoute, Router } from '@angular/router';
import { RaiseFundApplicationProcessModule } from '../../../core/enum/raise-fund-application-process-module.enum';
import { RaiseFundApplicationProcessStatusType } from '../../../core/enum/raise-fund-application-process-status-type.enum';
import { FundRaiseItem } from '../../../core/models/raise/fund-raise-item';
import { finalize } from 'rxjs/operators';
import { RaiseService } from '../../../core/services/api/raise.service';
import { ErrorService } from '../../../core/services/error.service';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UploadDocumentComponent } from '../../signup/documents/upload-document/upload-document.component';
import { BlockUiService } from '../../../shared/services/blockUi/block-ui.service';
import { FileValidationService } from '../../../core/services/file-validation.service';
import { FileUploadOption } from '../../../core/models/attachment/file-upload-option';

@Component({
    selector: 'app-application-process-list',
    templateUrl: './application-process-list.component.html',
    styleUrls: ['./application-process-list.component.scss'],
    standalone: false
})
export class ApplicationProcessListComponent {
  @Input() fundRaiseProcess: FundRaiseProcess;
  @Input() tabContent: TabContent;
  @Input() fundRaiseApplicationId: number;
  @Input() campaignCreated: boolean;
  @Output() next = new EventEmitter();
  @Output() refresh = new EventEmitter();

  isLoading: boolean = false;

  RaiseFundApplicationProcessModule: typeof RaiseFundApplicationProcessModule =
    RaiseFundApplicationProcessModule;
  RaiseFundApplicationActionType: typeof RaiseFundApplicationActionType =
    RaiseFundApplicationActionType;
  RaiseFundApplicationProcessStatusType: typeof RaiseFundApplicationProcessStatusType =
    RaiseFundApplicationProcessStatusType;

  isDraftItem(item: FundRaiseItem): boolean {
    return (item.raiseFundApplicationActionType == RaiseFundApplicationActionType.CreateCampaign || item.raiseFundApplicationActionType == RaiseFundApplicationActionType.CreateSummaryPage) && [RaiseFundApplicationProcessStatusType.None, RaiseFundApplicationProcessStatusType.Draft].includes(item.raiseFundApplicationProcessStatusType);
  }

  isDraftProcess(): boolean {
    return this.fundRaiseProcess?.raiseFundApplicationActionType == RaiseFundApplicationActionType.CreateCampaign && [RaiseFundApplicationProcessStatusType.None, RaiseFundApplicationProcessStatusType.Draft].includes(this.fundRaiseProcess.raiseFundApplicationProcessStatusType);
  }

  constructor(
    private popupService: PopupService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private raiseService: RaiseService,
    private sanitizer: DomSanitizer,
    private errorService: ErrorService,
    private modalService: NgbModal,
    private blockUiService: BlockUiService,
    private fileValidationService: FileValidationService,
  ) {}

  async selectFile(file: File, item: FundRaiseItem) {
    let uploadOption: FileUploadOption = {
      fileType: PdfAndXlsxFileFormat,
      fileSizeLimit: 30 * 1024 * 1024,
      errorMsg: 'common.onlyPdfAndXlasxAccepted',
    };

    if (item.raiseFundApplicationActionType == RaiseFundApplicationActionType.UploadCampaignVideo) {
      uploadOption = {
        fileType: VideoFileFormat,
        errorMsg: 'common.onlyVideoAccepted',
      };
    }

    if (await this.fileValidationService.validFile(file, uploadOption)) {
      item.attachment = file;
      item.attachmentUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
      this.uploadAttachment(item, item.id);
    }
  }

  uploadAttachment(item: FundRaiseItem, stepId: number, documentName?: string) {
    this.blockUiService.open();

    this.raiseService
      .uploadAttachment(item.attachment, this.fundRaiseApplicationId, stepId, documentName)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: (data) => {
          item.attachmentUrl = data.url;
          this.refresh.emit();
        },
        error: (err) => {
          this.errorService.showError(err);
          this.deleteAttachment(stepId);
        },
      });
  }

  removeAttachment(document: Attachment) {
    document.file = null;
    this.refresh.emit();
  }

  viewDetails(url: string) {
    window.open(url);
  }

  nextStep() {
    if (
      this.tabContent.tab == RaiseFundApplicationProcessModule.OfferCreation &&
      !this.campaignCreated
    ) {
      this.router.navigate(['/raise/new-campaign']);
    }

    this.next.emit();
  }

  getAcceptType(raiseFundApplicationActionType: RaiseFundApplicationActionType) {
    return raiseFundApplicationActionType == RaiseFundApplicationActionType.UploadDocument
      ? PdfAndXlsxFileFormat.toString()
      : VideoFileFormat.toString();
  }

  makePayment() {
    this.router.navigate(['make-payment', this.fundRaiseApplicationId], {
      relativeTo: this.activatedRoute,
    });
  }

  uploadAdditionalDocument(item: FundRaiseItem) {
    const option: any = {
      size: 'md',
      centered: true,
      windowClass: 'modal-550',
      backdropClass: 'backdrop-popup',
    };

    let document = {} as Attachment;
    const modalRef = this.modalService.open(UploadDocumentComponent, option);
    modalRef.componentInstance.document = document;
    modalRef.componentInstance.returnFile = true;
    modalRef.componentInstance.fileTypes = PdfAndXlsxFileFormat;
    modalRef.componentInstance.fileSizeLimit = 31457280;
    modalRef.result.then(
      (document: Attachment) => {
        item.attachment = document.file;
        this.uploadAttachment(item, item.id, document.fileName);
      },
      () => {},
    );
  }

  removeAttachmentByAttachmentId(attachmentId: number, parentId: number) {
    this.popupService.confirm('company.confirmDeleteDocument').then(
      () => {
        this.blockUiService.open();

        this.raiseService
          .deleteAttachmentByAttachmentId(this.fundRaiseApplicationId, parentId, attachmentId)
          .pipe(finalize(() => this.blockUiService.close()))
          .subscribe({
            next: () => {
              this.refresh.emit();
            },
            error: (err) => {
              this.errorService.showError(err);
            },
          });
      },
      () => {},
    );
  }

  deleteAttachment(stepId: number) {
    this.blockUiService.open();

    this.raiseService
      .deleteAttachment(this.fundRaiseApplicationId, stepId)
      .pipe(finalize(() => this.blockUiService.close()))
      .subscribe({
        next: (data) => {
          this.refresh.emit();
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }
}
